import React, {
    Dispatch,
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useReducer,
} from 'react';
import errorReducer, { TAction, IErrorState } from './reducers/ErrorReducer';

export type THomeAddressErrorData = {
    HomeAddressLine1: IErrorState;
    HomeAddressLine2: IErrorState;
    HomeCity: IErrorState;
    HomeState: IErrorState;
    HomeZipCode: IErrorState;
};

interface IHomeAddressErrorProviderValue {
    homeAddressError: Record<keyof THomeAddressErrorData, IErrorState>;
    dispatch: Dispatch<TAction>;
}

const HomeAddressErrorContext = createContext<IHomeAddressErrorProviderValue>({
    homeAddressError: {
        HomeAddressLine1: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeAddressLine2: {
            isInError: false,
            isValidValue: true,
            required: false,
        },
        HomeCity: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeState: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeZipCode: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
    },
    dispatch: () => {},
});

export default function HomeAddressErrorProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [homeAddressError, dispatch] = useReducer(errorReducer, {
        HomeAddressLine1: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeAddressLine2: {
            isInError: false,
            isValidValue: true,
            required: false,
        },
        HomeCity: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeState: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        HomeZipCode: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
    });

    return (
        <HomeAddressErrorContext.Provider
            value={useMemo(
                () => ({
                    homeAddressError,
                    dispatch,
                }),
                [homeAddressError, dispatch],
            )}
        >
            {children}
        </HomeAddressErrorContext.Provider>
    );
}

export const useHomeAddressError = () => useContext(HomeAddressErrorContext);
